import React, { useState, useEffect, useContext, useRef } from "react";
import Header from "../../elements/Header";
import { useNavigate } from "react-router-dom";
import { getAuth } from "firebase/auth";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  updateDoc,
  addDoc,
  writeBatch,
} from "firebase/firestore";
import { useFirestore } from "../../firebase/FirestoreContext";
import handleQuantityChange from "../../functions/handleQuantityChange";
import handleDeleteItem from "../../functions/handleDeleteItem";
import { EnvContext } from "../../context/EnvContext";
import config from "../../config";
import { logAnalyticsEvent } from "../../firebase/firebaseConfig";
import Footer from "../../elements/Footer";
import FAQ from "../../elements/FAQ";
import ShippingCheckoutBanner from "../../elements/ShippingCheckoutBanner";
import NavigationSimple from "../../elements/NavigationSimple";
import analyticsHelper from "../../utils/analyticsHelper";
import { formatMoney } from "../../utils/format";
import { getUserDocuments } from "../../hooks/useDocument";
import useCartValidations from "../../hooks/useCartValidations";
import NoticeBanner from "../../elements/NoticeBanner";
import useCart from "../../hooks/useCart";
import CartItem from "../../elements/CartItem";
import SessionReplayContext from "../../context/SessionReplayContext";
import StickyFooter from "../../elements/StickyFooter";
import CheckoutAuthBanner from "../../elements/CheckoutAuthBanner";
import styled from "@emotion/styled";
import { pluralizeWithCount } from "../../functions/pluralize";

const Container = styled.div`
  max-width: 1140px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Cart = () => {
  const environment = useContext(EnvContext);
  const {
    isReady,
    appliedDiscount,
    cartItems,
    refresh: refreshCart,
    refreshedAtTime,
    setAppliedDiscount,
    setCartItems,
    summary,
    cart,
  } = useCart();
  const { validateCart } = useCartValidations();

  const { enableSessionReplay } = useContext(SessionReplayContext);

  useEffect(() => {
    enableSessionReplay();
  }, [enableSessionReplay]);

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [cart_sooperstock_ID, setCart_sooperstock_ID] = useState(null);
  const [disabledItems, setDisabledItems] = useState({});
  const { cartErrors: cachedCartErrors } = useCartValidations();
  const [localCartErrors, setCartErrors] = useState([]);

  const cartErrors = localCartErrors.length
    ? localCartErrors
    : cachedCartErrors;

  const [discountCode, setDiscountCode] = useState(""); // Input for discount code
  const [discountMessage, setDiscountMessage] = useState("");
  const [discountMessageStyle, setDiscountMessageStyle] = useState({
    color: "black",
  });
  const [isDiscountLoading, setIsDiscountLoading] = useState(false);
  const [isCheckoutLoading, setIsCheckoutLoading] = useState(false);

  const [showStickyFooter, setShowStickyFooter] = useState(false);
  const checkoutButtonRef = useRef(null);

  const firestore = useFirestore();
  const auth = getAuth();
  const user = auth.currentUser;

  // environment
  const collectionPath =
    environment === "staging"
      ? config.environment.staging.collectionPath
      : config.environment.production.collectionPath;

  useEffect(() => {
    function checkCheckoutVisibility() {
      if (!checkoutButtonRef.current) return;
      const rect = checkoutButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener("scroll", checkCheckoutVisibility, {
      passive: true,
    });
    window.addEventListener("resize", checkCheckoutVisibility);

    const timeoutId = setTimeout(checkCheckoutVisibility, 50);

    return () => {
      window.removeEventListener("scroll", checkCheckoutVisibility);
      window.removeEventListener("resize", checkCheckoutVisibility);
      clearTimeout(timeoutId);
    };
  }, []);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!checkoutButtonRef.current) return;
      const rect = checkoutButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [cartItems.length]);

  useEffect(() => {
    function checkCheckoutVisibility() {
      if (!checkoutButtonRef.current) return;
      const rect = checkoutButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }
    window.addEventListener('scroll', checkCheckoutVisibility, { passive: true });
    window.addEventListener('resize', checkCheckoutVisibility);

    const timeoutId = setTimeout(checkCheckoutVisibility, 50);

    return () => {
      window.removeEventListener('scroll', checkCheckoutVisibility);
      window.removeEventListener('resize', checkCheckoutVisibility);
      clearTimeout(timeoutId);
    };
  }, []); 


  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (!checkoutButtonRef.current) return;
      const rect = checkoutButtonRef.current.getBoundingClientRect();
      const isVisible = rect.bottom > 0 && rect.top < window.innerHeight;
      setShowStickyFooter(!isVisible);
    }, 50);
    return () => clearTimeout(timeoutId);
  }, [cartItems.length]);
  
  useEffect(() => {
    if (!user) {
      navigate("/");
    }
  }, [user, navigate]);

  useEffect(() => {
    setIsLoading(!isReady);
  }, [isReady]);

  //
  //
  // SHIPPING SETTINGS
  const fetchUserCart = async (firestore) => {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      return null;
    }

    const cartQuery = query(
      collection(firestore, `${collectionPath}Carts/CartsList/DataBase`),
      where("user_id", "==", user.uid),
      where("status", "==", "ordering"),
    );

    const cartQuerySnapshot = await getDocs(cartQuery);

    if (!cartQuerySnapshot.empty) {
      setCart_sooperstock_ID(
        cartQuerySnapshot.docs[0].data()?.cart_sooperstock_ID + 123,
      );
      return cartQuerySnapshot.docs[0]; // Return the first cart document
    }

    return null;
  };

  useEffect(() => {
    if (cartItems.length > 0 && summary.total > 0.01) {
      analyticsHelper.viewCart({
        cartItems,
        summary,
        cart_sooperstock_ID,
      });
    }
  }, [cartItems.length, cart_sooperstock_ID, summary.total]);

  // DISCOUNT CODE

  const handleApplyDiscountCode = async () => {
    setIsDiscountLoading(true);
    setDiscountMessage("Checking code...");
    setDiscountMessageStyle({ color: "black" });

    if (!user || !user.email) {
      setDiscountMessage("Please sign in to apply this code");
      setDiscountMessageStyle({ color: "red" });
      setIsDiscountLoading(false);
      return;
    }

    const discountsCollection = collection(
      firestore,
      `${collectionPath}Marketing/DiscountsCodes/DataBase`,
    );
    const discountQuery = query(
      discountsCollection,
      where("code", "==", discountCode.toUpperCase()),
    );

    try {
      const querySnapshot = await getDocs(discountQuery);

      if (!querySnapshot.empty) {
        const docSnap = querySnapshot.docs[0];
        const data = docSnap.data();

        // Validate if discount code is active
        const today = new Date();
        if (data.startDate.toDate() > today || today > data.endDate.toDate()) {
          setDiscountMessage("Discount code expired");
          setDiscountMessageStyle({ color: "red" });
          setIsDiscountLoading(false);
          return;
        }

        // Check if minimum purchase amount is met
        if (
          data.minimumPurchaseAmount &&
          summary.subtotal < data.minimumPurchaseAmount
        ) {
          setDiscountMessage(
            `Minimum purchase of $${data.minimumPurchaseAmount} required.`,
          );
          setDiscountMessageStyle({ color: "red" });
          setIsDiscountLoading(false);
          return;
        }

        // Fetch the user's cart to get cartID
        const cartDoc = await fetchUserCart(firestore);
        if (!cartDoc) {
          setDiscountMessage("No active cart found.");
          setDiscountMessageStyle({ color: "red" });
          setIsDiscountLoading(false);
          return;
        }

        const cartID = cartDoc.id; // Extract cartID from cartDoc

        // Overwrite existing discount code (remove any previous one for this cart)

        const oldUsages = await getUserDocuments(
          {
            firestore,
            user,
            path: "DiscountCodeUsage",
          },
          where("cartID", "==", cartID),
        );

        const batch = writeBatch(firestore);

        oldUsages.forEach((doc) => {
          batch.delete(doc.ref); // Delete all existing records for this cart
        });

        await batch.commit(); // Execute the batch deletion

        const usages = await getUserDocuments(
          {
            firestore,
            user,
            path: "DiscountCodeUsage",
          },
          where("code", "==", discountCode),
          where("status", "==", "used"),
        );

        // Check the user's usage count (USED only)
        const usedCount = usages.size;

        if (usedCount >= data.limitPerUser) {
          setDiscountMessage(
            `This code can only be used ${data.limitPerUser} times.`,
          );
          setDiscountMessageStyle({ color: "red" });
          setIsDiscountLoading(false);
          return;
        }

        // Calculate the discount amount with limits
        let calculatedDiscount = (summary.subtotal * data.amount) / 100; // Assume percentage discount
        if (data.discountType === "fixed") {
          calculatedDiscount = data.amount; // Fixed amount discount
        }
        if (data.discountAmountLimit) {
          calculatedDiscount = Math.min(
            calculatedDiscount,
            data.discountAmountLimit,
          ); // Apply max limit
        }

        // Store a pending record
        try {
          const usageCollection = collection(
            firestore,
            `${collectionPath}DiscountCodeUsage`,
          );
          await addDoc(usageCollection, {
            user_uid: user?.uid,
            user_email: user?.email || "",
            code: discountCode,
            cartID,
            status: "pending",
            appliedAt: new Date(),
          });
        } catch {
          console.error("FAILED TO CREATE USGE");
        }

        // Apply discount to the cart
        const cartDocRef = doc(
          firestore,
          `${collectionPath}Carts/CartsList/DataBase`,
          cartID,
        );
        try {
          await updateDoc(cartDocRef, {
            applied_discount_name: data.code,
            applied_discount_id: data.id,
            applied_discount_percentage: data.amount,
            applied_discount_amount: calculatedDiscount,
            applied_discount_max_amount: data.discountAmountLimit || 0,
            applied_discount_minimum_purchase_amount:
              data.minimumPurchaseAmount || 0,
          });
        } catch {
          console.error("FAILED TO UPDATE CART");
        }

        // Update UI state
        setAppliedDiscount({
          code: data.code,
          name: data.name,
          discount: data.amount,
          discountAmount: calculatedDiscount,
          maxAmount: data.discountAmountLimit,
        });

        setDiscountMessage(`Promo applied!`);
        setDiscountMessageStyle({ color: "black" });
      } else {
        setDiscountMessage("Invalid discount code");
        setDiscountMessageStyle({ color: "red" });
      }
    } catch (error) {
      console.error("Error applying discount:", error);
      setDiscountMessage("Failed to apply discount code");
      setDiscountMessageStyle({ color: "red" });
    } finally {
      setIsDiscountLoading(false);
    }
  };

  useEffect(() => {
    if (summary.discountAmount > 0) {
      setDiscountMessage(`Promo applied!`);
      setDiscountMessageStyle({ color: "black" });
    } else {
      setDiscountMessage("");
    }
  }, [summary.discountAmount]);

  const handleRemoveDiscountCode = async () => {
    try {
      // Fetch the user's cart to get cartID
      const cartDoc = await fetchUserCart(firestore);
      if (!cartDoc) return;

      const cartID = cartDoc.id;

      // Remove from DiscountCodeUsage collection
      const oldUsages = await getUserDocuments(
        {
          firestore,
          user,
          path: "DiscountCodeUsage",
        },
        where("cartID", "==", cartID),
      );

      const batch = writeBatch(firestore);

      oldUsages.forEach((doc) => {
        batch.delete(doc.ref); // Delete all existing records for this cart
      });

      // Remove discount from the cart document
      const cartDocRef = doc(
        firestore,
        `${collectionPath}Carts/CartsList/DataBase`,
        cartID,
      );
      await updateDoc(cartDocRef, {
        applied_discount_name: "",
        applied_discount_id: "",
        applied_discount_percentage: 0,
        applied_discount_amount: 0,
        applied_discount_max_amount: 0,
        applied_discount_minimum_purchase_amount: 0,
      });

      // Update UI state
      setAppliedDiscount(null);
      setDiscountMessageStyle({ color: "black" });
      setDiscountCode("");
      setDiscountMessage("");
    } catch (error) {
      console.error("Error removing discount code:", error);
      setDiscountMessage("Failed to remove discount code");
      setDiscountMessageStyle({ color: "red" });
    }
  };

  //
  //
  // GO TO CHECKOUT

  const handleCheckout = async () => {
    setIsCheckoutLoading(true);

    // analytics
    logAnalyticsEvent("cart_page_procced_to_checkout_button", {
      content_type: "checkout_button",
      page_title:
        environment === "staging" ? "STAGING - Cart Page" : "Cart Page",
      page_location: window.location.href,
      page_path: window.location.pathname,
      environment: environment,
      cart_sooperstock_ID: cart_sooperstock_ID,
      cart_items: cartItems.length,
      cart_total: summary.total,
      cart_perishable_amount: summary.perishableAmount,
    });

    try {
      // Update Firestore with the final amount and shipping price
      const cartDocRef = doc(
        firestore,
        `${collectionPath}Carts/CartsList/DataBase`,
        cartItems[0].cartID,
      );

      await updateDoc(cartDocRef, {
        subtotal: summary.itemTotal,
        shippingCost: summary.calculatedShippingCost,
        applied_discount_name: appliedDiscount?.code || "",
        applied_discount_percentage: appliedDiscount?.discount || 0,
        applied_discount_amount: summary.discountAmount,
        finalAmount: summary.total,
        applied_discount_max_amount: appliedDiscount?.maxAmount || 0,
        applied_discount_minimum_purchase_amount:
          appliedDiscount?.minimumPurchaseAmount || 0,
        perishableAmount: summary.perishableAmount,
      });

      // Navigate to the checkout page
      navigate("/checkout", {
        state: {
          cartID: cartItems.length ? cartItems[0].cartID : null,
          items: cartItems,
          cart_sooperstock_ID: cart_sooperstock_ID,
          subtotal: summary.itemTotal,
          shippingCost: summary.calculatedShippingCost,
          total: summary.total,
          freeShipingThreshold: 0,
          finalAmount: summary.total,
          perishableAmount: summary.perishableAmount,
        },
      });
      setIsCheckoutLoading(false);
    } catch (error) {
      console.error("Error updating cart data for checkout: ", error);
      setIsCheckoutLoading(false);
      alert("Failed to proceed to checkout. Please try again.");
    }
  };

  const quantityUpdateState = useRef({});
  const debounceTimers = useRef({});

  const handleQuantityUpdate = (item, quantityChange) => {
    // Guard: no update if disabled
    if (disabledItems[item.id]) return;

    // Use a ref to track the original quantity for this debounce session
    if (!quantityUpdateState.current[item.id]) {
      quantityUpdateState.current[item.id] = item.quantity;
    }

    // Optimistically update the UI immediately:
    const newQuantity = item.quantity + quantityChange;
    setCartItems((prevItems) =>
      prevItems.map((it) =>
        it.id === item.id ? { ...it, quantity: newQuantity } : it,
      ),
    );

    // Clear any existing debounce timer for this item
    if (debounceTimers.current[item.id]) {
      clearTimeout(debounceTimers.current[item.id]);
    }

    const timer = setTimeout(async () => {
      setDisabledItems((prev) => ({ ...prev, [item.id]: true }));
      // Get the latest quantity from cartItems (UI state)
      let latestQuantity = newQuantity;
      setCartItems((prevItems) => {
        const found = prevItems.find((it) => it.id === item.id);
        if (found) latestQuantity = found.quantity;
        return prevItems;
      });
      const originalQuantity = quantityUpdateState.current[item.id];
      const diff = latestQuantity - originalQuantity;
      if (latestQuantity < 1) {
        try {
          await handleDeleteItem(
            firestore,
            item.cartID,
            item.seller_product_id,
            originalQuantity,
            setCartItems,
            collectionPath,
          );
        } catch (error) {
          console.error("Error deleting item: ", error);
        } finally {
          setDisabledItems((prev) => ({ ...prev, [item.id]: false }));
          delete quantityUpdateState.current[item.id];
        }
        return;
      }
      try {
        if (diff !== 0) {
          await handleQuantityChange(
            firestore,
            item.cartID,
            item.seller_product_id,
            diff,
            setCartItems,
            collectionPath,
          );
        }
      } catch (error) {
        console.error("Error updating quantity in DB: ", error);
        // Revert the optimistic UI update if the DB call fails
        setCartItems((prevItems) =>
          prevItems.map((it) =>
            it.id === item.id ? { ...it, quantity: originalQuantity } : it,
          ),
        );
      } finally {
        setDisabledItems((prev) => ({ ...prev, [item.id]: false }));
        delete quantityUpdateState.current[item.id];
      }
    }, 1000);

    debounceTimers.current[item.id] = timer;
  };

  useEffect(() => {
    if (!refreshedAtTime) return () => {};
    if (!cart?.id) return () => {};

    const checkCart = async () => {
      const cartErrors = await validateCart(cart.id);
      if (cartErrors?.length) {
        setCartErrors(cartErrors);
        if (cartErrors?.length) refreshCart();
      }
    };
    checkCart();
  }, [refreshedAtTime, cart?.id]);

  if (isLoading) {
    return <div className="loading">Loading...</div>;
  }

  const itemCount = cartItems.reduce(
    (acc, item) => acc + (item.quantity || 1),
    0,
  );

  return (
    <div>
      <Header search={false} />
      <Container css={{ maxWidth: "1140px", margin: "0 auto" }}>
        <NavigationSimple color={"black"} />
        <ShippingCheckoutBanner totalAmount={summary.subtotal} />
        {!user?.email && <CheckoutAuthBanner onSignInComplete={refreshCart} />}
        <div style={styles.cartContainer}>
          {cartErrors.length > 0 && (
            <NoticeBanner
              message="We've made some changes to your cart."
              errors={cartErrors}
            />
          )}
          <h2 style={styles.title}>My Cart</h2>
          <div style={{ height: 20 }} />
          <h3 style={{ fontSize: "1rem", fontWeight: 400 }}>
            Total
            <b
              style={{
                background: "black",
                padding: "5px 10px",
                color: "white",
                borderRadius: 5,
                margin: "0px 4px",
              }}
            >
              {itemCount}
            </b>
            {cartItems.length === 1 ? "item" : "items"}
          </h3>
          {cartItems.length === 0 && (
            <div
              style={{
                textAlign: "left",
                paddingTop: 20,
                fontSize: "1rem",
                color: "gray",
                margin: "10px 0 25px 5px",
              }}
            >
              Your cart is empty
            </div>
          )}
          <div
            style={{
              fontSize: "1 rem",
              color: "black",
              marginTop: 10,
              marginBottom: 6,
            }}
          >
            Shipment 1 of 1 ({pluralizeWithCount(itemCount, "item")})
          </div>
          {cartItems.map((item) => {
            const error = cartErrors.find(
              ({ cartItem }) => cartItem?.id === item.id,
            );

            return (
              <div>
                <CartItem
                  key={item.id}
                  item={item}
                  onDecrease={async () => {
                    if (!disabledItems[item.id]) {
                      await handleQuantityUpdate(item, -1); // Decrease quantity
                    }
                  }}
                  onIncrease={async () => {
                    if (!disabledItems[item.id]) {
                      await handleQuantityUpdate(item, 1); // Increase quantity
                    }
                  }}
                  onDelete={async () => {
                    if (!disabledItems[item.id]) {
                      setDisabledItems((prev) => ({
                        ...prev,
                        [item.id]: true,
                      })); // Disable the delete button for this item

                      try {
                        await handleDeleteItem(
                          firestore,
                          item.cartID,
                          item.seller_product_id,
                          item.quantity,
                          setCartItems,
                          collectionPath,
                        );
                        refreshCart();
                      } catch (error) {
                        console.error("Error deleting item: ", error);
                      } finally {
                        setDisabledItems((prev) => ({
                          ...prev,
                          [item.id]: false,
                        })); // Re-enable the delete button after the operation
                      }
                    }
                  }}
                  disabled={disabledItems[item.id]}
                  error={error}
                />
              </div>
            );
          })}
          {/* Discount Code Section */}
          <div style={{ padding: "20px 0px 20px 0" }}>
            <div
              style={{
                ...styles.summaryRow,
                display: "flex",
                justifyContent: "space-between",
                flexDirection: "column",
                fontSize: "1rem",
              }}
            >
              <div style={{ marginBottom: 10 }}>
                <span>Promo Code:</span>
              </div>

              <div style={{ display: "flex", flexDirection: "row" }}>
                {appliedDiscount ? (
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", flexDirection: "row" }}>
                      <div style={{}}>
                        <input
                          type="text"
                          value={appliedDiscount.code}
                          style={{
                            ...styles.discountInput,
                            borderRadius: 8,
                            width: "100%",
                          }}
                          readOnly
                        />
                      </div>
                      <div
                        onClick={() => {
                          handleRemoveDiscountCode();
                        }}
                        style={{
                          ...styles.discountButton,
                          backgroundColor: isDiscountLoading ? "gray" : "black",
                          opacity: isDiscountLoading ? 0.5 : 1,
                          cursor: "pointer",
                          borderRadius: 8,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Clear &#10005;
                      </div>
                    </div>
                    <div style={{ paddingLeft: 10 }}>
                      {discountMessage && (
                        <p style={discountMessageStyle}>{discountMessage}</p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div style={{ flex: 1 }}>
                    <div
                      style={{ display: "flex", flexDirection: "row", flex: 1 }}
                    >
                      <input
                        type="text"
                        value={discountCode}
                        onChange={(e) => setDiscountCode(e.target.value)}
                        placeholder=""
                        style={{
                          ...styles.discountInput,
                          borderRadius: 8,
                          width: "100%",
                        }}
                      />
                      <div
                        onClick={handleApplyDiscountCode}
                        disabled={isDiscountLoading}
                        style={{
                          ...styles.discountButton,
                          backgroundColor: isDiscountLoading ? "gray" : "black",
                          opacity: isDiscountLoading ? 0.5 : 1,
                          borderRadius: 8,
                          cursor: "pointer",
                        }}
                      >
                        {isDiscountLoading ? "Checking..." : "Apply"}
                      </div>
                    </div>
                    {
                      <div style={{ ...discountMessageStyle }}>
                        {discountMessage}
                      </div>
                    }
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {/* Summary section */}
        <div style={{ ...styles.summary, fontSize: "1rem" }}>
          <h3 style={{ fontWeight: "1.1rem", paddingBottom: 10 }}>Summary</h3>
          <div style={styles.summaryRow}>
            <span>Items</span>
            <span
              style={{
                borderBottom: appliedDiscount?.discount
                  ? ""
                  : "1px solid #b3b3b3",
                paddingLeft: 15,
              }}
            >
              ${summary.itemTotal.toFixed(2)}
            </span>
          </div>
          {appliedDiscount && (
            <div style={styles.summaryRow}>
              <span>Promotion ({`${appliedDiscount?.discount}% off`})</span>
              <span
                style={{
                  borderBottom: appliedDiscount?.discount
                    ? "1px solid #b3b3b3"
                    : "",
                }}
              >
                - {formatMoney(summary.discountAmount)}
              </span>
            </div>
          )}
          <div style={styles.summaryRow}>
            <span>Subtotal:</span>
            <span style={{}}>${summary.subtotal.toFixed(2)}</span>
          </div>
          <div style={styles.summaryRow}>
            <span>Shipping:</span>
            <span
              style={{
                borderBottom:
                  summary.perishableAmount > 0 ? "" : "1px solid #b3b3b3",
                paddingLeft: 15,
              }}
            >
              {summary.calculatedShippingCost === 0
                ? "FREE"
                : `$${summary.calculatedShippingCost.toFixed(2)}`}
            </span>
          </div>
          {summary.perishableAmount > 0 && (
            <div style={{ ...styles.summaryRow }}>
              <span style={styles.subSummmaryItem}> + Cold Shipping Fee:</span>
              <span
                style={{ borderBottom: "1px solid #b3b3b3", paddingLeft: 15 }}
              >
                {formatMoney(summary.perishableAmount)}
              </span>
            </div>
          )}
          <div style={{ ...styles.summaryRow, fontWeight: "600" }}>
            <span>Total:</span>
            <span style={{}}>${summary.total.toFixed(2)}</span>
          </div>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <button
              ref={checkoutButtonRef}
              style={{
                ...styles.checkoutButton,
                ...(cartItems.length === 0
                  ? {
                      backgroundColor: "gray",
                      cursor: "not-allowed",
                      color: "rgba(255,255,255,.3)",
                    }
                  : {}),
              }}
              onClick={handleCheckout}
              disabled={cartItems.length === 0 || isCheckoutLoading}
            >
              {isCheckoutLoading ? "Processing..." : "Checkout"}
            </button>
          </div>

          <div style={{ height: 20 }} />
        </div>
      </Container>
      <FAQ />
      <Footer />
      {showStickyFooter && (
        <StickyFooter>
          <button
            style={{
              ...styles.checkoutButtonSticky,
              ...(cartItems.length === 0
                ? {
                    backgroundColor: "gray",
                    cursor: "not-allowed",
                    color: "rgba(255,255,255,.3)",
                  }
                : {}),
            }}
            onClick={handleCheckout}
            disabled={cartItems.length === 0 || isCheckoutLoading}
          >
            {isCheckoutLoading ? "Processing..." : "Checkout"}
          </button>
        </StickyFooter>
      )}
    </div>
  );
};

const styles = {
  cartContainer: {
    backgroundColor: "#fff",
    padding: "0 15px",
  },
  title: {
    color: "black",
    fontSize: "1.2rem",
    fontWeight: 700,
  },
  quantityButton: {
    width: "40px",
    height: "100%",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    cursor: "pointer",
    fontSize: "20px",
  },
  summary: {
    marginTop: "10px",
    padding: "10px 15px",
    background: "rgba(59, 114, 254, 0.05)",
  },
  summaryTitle: {
    fontSize: "14px",
    color: "gray",
    marginBottom: "10px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: "5px",
    padding: "3px 0",
  },
  subSummmaryItem: {
    fontSize: "14px",
    color: "gray",
    marginLeft: "5px",
  },
  subtotalRow: {
    display: "flex",
    justifyContent: "space-between",
    marginTop: "10px",
    fontWeight: "bold",
  },
  total: {
    color: "black",
  },
  checkoutButton: {
    width: "100%",
    maxWidth: 600,
    backgroundColor: "#3b72fe",
    color: "white",
    padding: "10px 10px",
    marginTop: "20px",
    border: "none",
    cursor: "pointer",
    fontSize: "1.2rem",
    borderRadius: "50px",
    fontWeight: "700",
  },
  checkoutButtonSticky: {
    width: "100%",
    maxWidth: 600,
    backgroundColor: "#3b72fe",
    color: "white",
    padding: "10px 10px",
    border: "none",
    cursor: "pointer",
    fontSize: "1.2rem",
    borderRadius: "50px",
    fontWeight: "700",
    marginLeft: "10px",
    marginRight: "10px",
  },
  discountContainer: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  discountButton: {
    marginLeft: "10px",
    padding: "5px 15px",
    height: 42,
    justifyContent: "center",
    display: "flex",
    alignItems: "center",
    color: "white",
  },
};

export default Cart;
